export const parseDate = (dateString: string): Date | null => {
  if (typeof dateString === 'string') {
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/

    if (dateOnlyRegex.test(dateString)) {
      const [year, month, day] = dateString.split('-')
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    }

    return null
  }
  return null
}

// Función para formatear la fecha manualmente sin "de"
const formatDateWithoutDe = (date: Date, options: Intl.DateTimeFormatOptions, locale: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat(locale, options)
    const parts = formatter.formatToParts(date)

    const dayName = parts.find((part) => part.type === 'weekday')?.value || ''
    const day = parts.find((part) => part.type === 'day')?.value || ''
    const monthName = parts.find((part) => part.type === 'month')?.value || ''
    const year = parts.find((part) => part.type === 'year')?.value || ''
    const hour = parts.find((part) => part.type === 'hour')?.value.padStart(2, '0') || ''
    const minute = parts.find((part) => part.type === 'minute')?.value.padStart(2, '0') || ''
    const second = parts.find((part) => part.type === 'second')?.value.padStart(2, '0') || ''

    // Construcción de la fecha sin los "de"
    return `${dayName}, ${day} ${monthName} ${year} ${hour ? `${hour}:${minute}` : ''}${
      second ? `:${second}` : ''
    }`
  } catch (error) {
    console.error('Error formateando la fecha:', error)
    // Retorna la fecha original si hay un error en el formateo
    return date.toLocaleString(locale, options)
  }
}

export const getDay = (
  dateString: string,
  hoursAndMinutes = false,
  hoursAndMinutesAndSeconds = false,
  locale_param = null,
  without_de = false,
  short_format = true
): string | undefined | any => {
  let date = parseDate(dateString)
  if (!date) {
    date = new Date(dateString)
  }

  if (date) {
    const useUTC = dateString?.includes('Z')

    // Utilizar Intl.DateTimeFormat con el idioma dinámico
    const options: Intl.DateTimeFormatOptions = {
      weekday: short_format ? 'short' : 'long',
      year: 'numeric',
      month: short_format ? 'short' : 'long',
      day: 'numeric',
      ...((hoursAndMinutes || hoursAndMinutesAndSeconds) && {
        hour: 'numeric',
        minute: '2-digit',
        ...(hoursAndMinutesAndSeconds && { second: '2-digit' }),
      }),
      timeZone: useUTC ? 'UTC' : undefined,
    }

    let locale = locale_param || localStorage.getItem('language')
    try {
      if (without_de) {
        // Usa la función para formatear la fecha sin "de"
        return formatDateWithoutDe(date!, options, locale || 'es-AR')
      } else {
        return new Intl.DateTimeFormat(locale || 'es-AR', options).format(date)
      }
    } catch (error) {
      console.error(`Error formatting date with locale '${locale}':`, error)
      const fallbackFormatter = new Intl.DateTimeFormat('es-AR', options)
      return fallbackFormatter.format(date)
    }
  }
}

export const getDayWithTimeZone = (
  dateString: string | null,
  hoursAndMinutes = false,
  hoursAndMinutesAndSeconds = false,
  timeZone = 'UTC',
  locale_param = null,
  without_de = false,
  short_format = true
): string | undefined | any => {
  let date = parseDate(dateString ?? '')
  if (!date && dateString) {
    date = new Date(dateString)
  }

  const useUTC = dateString?.includes('Z')
  if (!useUTC && dateString) {
    date = new Date(dateString + 'Z')
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    weekday: short_format ? 'short' : 'long',
    year: 'numeric',
    month: short_format ? 'short' : 'long',
    day: 'numeric',
    ...((hoursAndMinutes || hoursAndMinutesAndSeconds) && {
      hour: 'numeric',
      minute: '2-digit',
      ...(hoursAndMinutesAndSeconds && { second: '2-digit' }),
    }),
    hour12: false,
  }

  let locale = locale_param || localStorage.getItem('language')
  try {
    if (without_de) {
      // Usa la función para formatear la fecha sin "de"
      return formatDateWithoutDe(date!, options, locale || 'es-AR')
    } else {
      return date ? new Intl.DateTimeFormat(locale || 'es-AR', options).format(date) : ''
    }
  } catch (error) {
    console.error(`Error formatting date with locale '${locale}':`, error)
    const fallbackFormatter = new Intl.DateTimeFormat('es-AR', options)
    return date ? fallbackFormatter.format(date) : ''
  }
}
